import React, {useCallback} from "react";
import {Button} from "fearless-common-ui";
import {useField, useFormikContext} from "formik";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Pages } from "../../../components/layout";
import {backendEndpoints} from "../../../libs/backendEndpoints";
import {BackendForm} from "../../../components/forms";
import FloatingLabelField from "../../../components/forms/FloatingLabelField";
import Select from "../../../components/forms/Select";
import {useConfirm} from "../../../components/contexts/ConfirmationModalContext";
import SelectManyUsers from "../../../components/admin/SelectManyUsers";
import Description from "../../../components/Description";
import {toast} from "react-hot-toast";
import {useNavigate} from "react-router";
import {adminRoutes} from "../../../utils/routes";

const viaOptions = [
  { value: 'database', label: 'In app notifications' },
];

function QuillTextareaEditor() {
  const [{ value },, { setValue }] = useField('html_message');

  const onChange = useCallback(
    (value: string) => setValue(value),
    [setValue],
  );
  return <ReactQuill theme="snow" value={value} onChange={onChange} />;
}

/**
 * Renders a SubmitContainer component that handles submitting the form.
 * The component includes asking the user for a confirmation
 *
 * @return {JSX.Element} A rendered container with the "submit" button.
 */
function SubmitContainer() {
  const { submitForm } = useFormikContext();
  const [{ value }] = useField('user_ids');
  const { openPromisify } = useConfirm();

  return (
    <div className="text-end">
      <Button
        disabled={value.length === 0}
        onClick={() => {
          openPromisify(
            'Are you sure you want to send it?',
            () => Promise.resolve(),
            true,
          ).then(() => {
            submitForm()
          });
        }}
      >
        {value.length === 0 ? 'Please select at least one user' : `Send notifications to ${value.length} users`}
      </Button>
    </div>
  );
}

/**
 * Renders a page with a form to send notifications to many users at once.
 */
export default function AdminSendNotificationsForm() {
  const navigate = useNavigate();

  return (
    <>
      <Pages.Centred width="sm">
        <div className="d-flex align-items-center">
          <h1 className="mb-0 me-3">Send notifications</h1>
        </div>
        <Description>Send notifications to many users at once.</Description>
        <BackendForm
          initialValues={{
            via: 'database',
            subject: '',
            html_message: '',
            user_ids: [],
          }}
          targetEndpoint={backendEndpoints.postBatchNotifications}
          targetMethod="POST"
          showFooter={false}
          onSuccess={(resp) => {
            toast.success(resp.message || 'Notifications sent!');
            navigate(adminRoutes.notifications);
          }}
        >
          <Select
            options={viaOptions} name="via" label="Via"
            helperText="Communications channel through which the users receive the message"
          />
          <FloatingLabelField
            name="subject"
            label="Summary"
            helperText="Text that will show before the user open the notification"
          />
          <h6 className="ps-4">Message</h6>
          <QuillTextareaEditor />
          <div className="pt-5" />
          <SelectManyUsers name="user_ids" />
          <BackendForm.Feedback showError />
          <SubmitContainer />
        </BackendForm>
      </Pages.Centred>
    </>
  );
}
